import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import { apiFetch, requestJson } from "../../../helpers/api";

type PlanillaRow = Record<string, unknown>;

interface PlanillaTabsProps {
  filas?: PlanillaRow[];
}

const datosDemo: PlanillaRow[] = [
  {
    LiquidacionId: 1,
    L: "A1",
    "Ord.x EMP": 1,
    "Apellidos, Nombres": "Ana González",
    "CI N°": "3.456.789",
    "Sucursal/ Sector": "Administración",
    Puesto: "Auxiliar",
    "TOTAL INGRESOS": 4500000,
    "Neto a Cobrar": 4300000,
  },
  {
    LiquidacionId: 2,
    L: "A2",
    "Ord.x EMP": 2,
    "Apellidos, Nombres": "Luis Benítez",
    "CI N°": "2.213.456",
    "Sucursal/ Sector": "Comercial",
    Puesto: "Vendedor",
    "TOTAL INGRESOS": 5200000,
    "Neto a Cobrar": 5000000,
  },
];

const formatMonto = (valor: number) =>
  valor.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const formatNumero = (valor: number) =>
  valor.toLocaleString("es-PY", { maximumFractionDigits: 0 });

const NOMBRES_MESES: Record<string, string> = {
  enero: "01",
  febrero: "02",
  marzo: "03",
  abril: "04",
  mayo: "05",
  junio: "06",
  julio: "07",
  agosto: "08",
  septiembre: "09",
  setiembre: "09",
  octubre: "10",
  noviembre: "11",
  diciembre: "12",
};

type PeriodoReciente = {
  valor: string;
  etiqueta: string;
  anho: string;
};

const generarPeriodosRecientes = (cantidad = 20): PeriodoReciente[] => {
  const periodos: PeriodoReciente[] = [];
  const actual = new Date();
  for (let i = 0; i <= cantidad; i += 1) {
    const fecha = new Date(actual.getFullYear(), actual.getMonth() - i, 1);
    const anho = fecha.getFullYear().toString();
    const mesNumero = (fecha.getMonth() + 1).toString().padStart(2, "0");
    const nombreMes = Object.entries(NOMBRES_MESES).find(([, numero]) => numero === mesNumero)?.[0];
    const nombreCapitalizado = nombreMes
      ? `${nombreMes.charAt(0).toUpperCase()}${nombreMes.slice(1)}`
      : "";
    periodos.push({
      valor: `${anho}-${mesNumero}`,
      etiqueta: `Año: ${anho} · Mes: ${mesNumero}${nombreCapitalizado ? ` (${nombreCapitalizado})` : ""}`,
      anho,
    });
  }
  return periodos;
};

type PeriodoMetadata = {
  valor: string;
  etiqueta: string;
  claveOrden: string;
};

const analizarPeriodo = (valor: string): PeriodoMetadata => {
  const texto = (valor ?? "").trim();
  if (!texto) {
    return { valor: "", etiqueta: "Sin periodo", claveOrden: "0000-00-Sin periodo" };
  }
  let mes: string | null = null;
  let anho: string | null = null;
  const minus = texto.toLowerCase();
  Object.entries(NOMBRES_MESES).forEach(([nombre, numero]) => {
    if (!mes && minus.includes(nombre)) {
      mes = numero;
    }
  });
  const matchYmPrimero = texto.match(/(\d{4})[-/](\d{1,2})/);
  if (matchYmPrimero) {
    anho = matchYmPrimero[1];
    mes = matchYmPrimero[2].padStart(2, "0");
  }
  const matchMy = texto.match(/(\d{1,2})[./](\d{4})/);
  if (matchMy) {
    mes = matchMy[1].padStart(2, "0");
    anho = matchMy[2];
  }
  if (!anho) {
    const matchAnho = texto.match(/(19|20)\d{2}/);
    if (matchAnho) {
      anho = matchAnho[0];
    }
  }
  const etiqueta =
    anho && mes
      ? `Año: ${anho} · Mes: ${mes} · ${texto}`
      : anho
        ? `Año: ${anho} · ${texto}`
        : texto;
  const claveOrden = `${anho ?? "0000"}-${mes ?? "00"}-${texto}`;
  return { valor: texto, etiqueta, claveOrden };
};

type CampoResumen = {
  campo: string;
  etiqueta: string;
  esMonto?: boolean;
  mostrarCero?: boolean;
};

const RESUMEN_INGRESOS_BASE: CampoResumen[] = [
  { campo: "TOTAL INGRESOS", etiqueta: "Total percibido", esMonto: true, mostrarCero: true },
  { campo: "Neto a Cobrar", etiqueta: "Neto a cobrar", esMonto: true, mostrarCero: true },
  { campo: "Salario Bruto", etiqueta: "Salario bruto", esMonto: true },
  { campo: "Salario Imponible", etiqueta: "Salario imponible", esMonto: true },
  { campo: "Salario Neto (devengado)", etiqueta: "Neto devengado", esMonto: true },
  { campo: "Jornal", etiqueta: "Jornal", esMonto: true },
];

const RESUMEN_INGRESOS_EXTRAS: CampoResumen[] = [
  { campo: "Total Ingresos Extras", etiqueta: "Total extras", esMonto: true, mostrarCero: true },
  { campo: "1.Feriados", etiqueta: "Feriados", esMonto: true },
  { campo: "2.Turnos dobles", etiqueta: "Turnos dobles", esMonto: true },
  { campo: "3.Feriados Dobles", etiqueta: "Feriados dobles", esMonto: true },
  { campo: "4.Dias libres trabajados", etiqueta: "Días libres", esMonto: true },
  { campo: "5. Fumigacion", etiqueta: "Fumigación", esMonto: true },
  { campo: "6. Campana", etiqueta: "Campaña", esMonto: true },
  { campo: "7. Auditoria", etiqueta: "Auditoría", esMonto: true },
  { campo: "8.Hs Extras", etiqueta: "Horas extra", esMonto: true },
  { campo: "9. Bonificacion - Plus Fijo", etiqueta: "Bonificación fija", esMonto: true },
  { campo: "10. Otros Ingresos", etiqueta: "Otros ingresos", esMonto: true },
];

const RESUMEN_DESCUENTOS: CampoResumen[] = [
  { campo: "Total descuento", etiqueta: "Total descuento", esMonto: true, mostrarCero: true },
  { campo: "Total Descuento Ausencias", etiqueta: "Ausencias", esMonto: true },
  { campo: "IPS - Aporte Obrero", etiqueta: "IPS obrero", esMonto: true },
  { campo: "total otros descuentos (incluye IPS)", etiqueta: "Otros desc.", esMonto: true },
  { campo: "1.Descuento Judicial", etiqueta: "Desc. judicial", esMonto: true },
  { campo: "2.Faltante en caja", etiqueta: "Faltante caja", esMonto: true },
  { campo: "3.Otros Descuentos", etiqueta: "Otros descuentos", esMonto: true },
  { campo: "4.Uniformes", etiqueta: "Uniformes", esMonto: true },
  { campo: "5.Consumición", etiqueta: "Consumición", esMonto: true },
  { campo: "7. Mermas", etiqueta: "Mermas", esMonto: true },
  { campo: "Adelanto complemento", etiqueta: "Adelanto complemento", esMonto: true },
  { campo: "ADELANTO DE 1 MILLON", etiqueta: "Adelanto 1M", esMonto: true },
  { campo: "6. Dif Adelantos", etiqueta: "Dif. adelantos", esMonto: true },
  { campo: "IPS NO DESCONTADO", etiqueta: "IPS no descontado", esMonto: true },
  { campo: "Entrega por Administracion", etiqueta: "Entrega administración", esMonto: true },
  { campo: "Diferencia devuelta a Celica", etiqueta: "Devuelto a Celica", esMonto: true },
  { campo: "VUELTO", etiqueta: "Vuelto", esMonto: true },
];

const RESUMEN_OBSERVACIONES: CampoResumen[] = [
  { campo: "OBSERVACION", etiqueta: "Obs. 1", mostrarCero: true },
  { campo: "OBSERVACION 2", etiqueta: "Obs. 2", mostrarCero: true },
  { campo: "Transferencia", etiqueta: "Transferencia", mostrarCero: true },
  { campo: "TRANSFERENCIA", etiqueta: "Transferencia (monto)", esMonto: true },
  { campo: "FIRMA", etiqueta: "Firma", mostrarCero: true },
  { campo: "Ingreso IPS Nueva Empresa", etiqueta: "Ingreso IPS", mostrarCero: true },
  { campo: "Salida de IPS Paradaise", etiqueta: "Salida IPS", mostrarCero: true },
  { campo: "Dias trabajados", etiqueta: "Días trabajados", mostrarCero: true },
  { campo: "Total dias no trabajados", etiqueta: "Días no trabajados", mostrarCero: true },
];

type ResumenItem = {
  etiqueta: string;
  valor: string;
};

const construirResumenCampos = (
  fila: PlanillaRow,
  definiciones: CampoResumen[],
  maxItems = 4,
): ResumenItem[] => {
  const items: ResumenItem[] = [];
  for (const definicion of definiciones) {
    const valor = fila?.[definicion.campo];
    const esValorTexto = typeof valor === "string";
    const textoLimpio = esValorTexto ? valor.trim() : "";
    const esVacio =
      valor === null || valor === undefined || (esValorTexto && textoLimpio.length === 0);
    if (esVacio) {
      continue;
    }
    if (definicion.esMonto) {
      const numero = Number(valor);
      if (!Number.isFinite(numero)) {
        continue;
      }
      if (!definicion.mostrarCero && numero === 0) {
        continue;
      }
      items.push({
        etiqueta: definicion.etiqueta,
        valor: `Gs. ${formatMonto(numero)}`,
      });
    } else {
      if (!definicion.mostrarCero && textoLimpio.length === 0) {
        continue;
      }
      items.push({
        etiqueta: definicion.etiqueta,
        valor: textoLimpio.length > 0 ? textoLimpio : "—",
      });
    }
    if (items.length >= maxItems) {
      break;
    }
  }
  return items;
};

const PlanillaTabs = ({ filas }: PlanillaTabsProps) => {
  const [busqueda, setBusqueda] = useState("");
  const [importModal, setImportModal] = useState(false);
  const [archivoImport, setArchivoImport] = useState<File | null>(null);
  const [tipoRecibo, setTipoRecibo] = useState<"SALARIO" | "AGUINALDO">("SALARIO");
  const [periodo, setPeriodo] = useState("");
  const [empresaId, setEmpresaId] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [importando, setImportando] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const [filasPlanilla, setFilasPlanilla] = useState<PlanillaRow[] | null>(null);
  const [cargandoPlanilla, setCargandoPlanilla] = useState(false);
  const [errorPlanilla, setErrorPlanilla] = useState<string | null>(null);
  const [ultimaActualizacion, setUltimaActualizacion] = useState<Date | null>(null);
  const [filtroSucursal, setFiltroSucursal] = useState<string>("");
  const [filtroEmpresa, setFiltroEmpresa] = useState<string>("");
  const [filtroPeriodo, setFiltroPeriodo] = useState<string>("");
  const [filtroFormaPago, setFiltroFormaPago] = useState<string>("");

  const cargarPlanilla = useCallback(async () => {
    setCargandoPlanilla(true);
    setErrorPlanilla(null);
    try {
      const data = await requestJson<{ ok: boolean; filas: Array<Record<string, unknown>> }>("/api/rrhh/planillas");
      setFilasPlanilla(Array.isArray(data?.filas) ? data.filas : []);
      setUltimaActualizacion(new Date());
    } catch (error) {
      setErrorPlanilla(error instanceof Error ? error.message : "No se pudo obtener la planilla.");
      setFilasPlanilla([]);
      setUltimaActualizacion(null);
    } finally {
      setCargandoPlanilla(false);
    }
  }, []);

  useEffect(() => {
    void cargarPlanilla();
  }, [cargarPlanilla]);

  const [paginaActual, setPaginaActual] = useState(1);
  const [filasPorPagina, setFilasPorPagina] = useState(5);

  const datasetBase = useMemo<PlanillaRow[]>(() => {
    if (filasPlanilla !== null) {
      return filasPlanilla;
    }
    if (Array.isArray(filas) && filas.length > 0) {
      return filas;
    }
    return datosDemo;
  }, [filas, filasPlanilla]);

  const periodosRecientes = useMemo(() => generarPeriodosRecientes(20), []);
  const anhosRecientes = useMemo(
    () => Array.from(new Set(periodosRecientes.map((periodo) => periodo.anho))),
    [periodosRecientes],
  );

  const obtenerValorCampo = (fila: PlanillaRow, campo: string) => String(fila?.[campo] ?? "").trim();

  type CampoFiltro = "Sucursal/ Sector" | "Empresa" | "Mes" | "Forma de pago";

  const filtrarDataset = (...condiciones: Array<[CampoFiltro, string]>) =>
    datasetBase.filter((fila) =>
      condiciones.every(([campo, valor]) => {
        if (!valor) {
          return true;
        }
        return obtenerValorCampo(fila, campo) === valor;
      }),
    );

  const datasetSucursales = useMemo(
    () => filtrarDataset(["Empresa", filtroEmpresa], ["Mes", filtroPeriodo], ["Forma de pago", filtroFormaPago]),
    [datasetBase, filtroEmpresa, filtroFormaPago, filtroPeriodo],
  );
  const datasetEmpresas = useMemo(
    () => filtrarDataset(["Sucursal/ Sector", filtroSucursal], ["Mes", filtroPeriodo], ["Forma de pago", filtroFormaPago]),
    [datasetBase, filtroFormaPago, filtroPeriodo, filtroSucursal],
  );
  const datasetPeriodos = useMemo(
    () => filtrarDataset(["Sucursal/ Sector", filtroSucursal], ["Empresa", filtroEmpresa], ["Forma de pago", filtroFormaPago]),
    [datasetBase, filtroEmpresa, filtroFormaPago, filtroSucursal],
  );
  const datasetFormasPago = useMemo(
    () => filtrarDataset(["Sucursal/ Sector", filtroSucursal], ["Empresa", filtroEmpresa], ["Mes", filtroPeriodo]),
    [datasetBase, filtroEmpresa, filtroPeriodo, filtroSucursal],
  );

  const obtenerValoresUnicos = (filas: PlanillaRow[], campo: CampoFiltro) => {
    const valores = new Set<string>();
    filas.forEach((fila) => {
      const valor = obtenerValorCampo(fila, campo);
      if (valor) {
        valores.add(valor);
      }
    });
    return Array.from(valores);
  };

  const opcionesFiltros = useMemo(
    () => {
      const sucursales = obtenerValoresUnicos(datasetSucursales, "Sucursal/ Sector").sort((a, b) =>
        a.localeCompare(b),
      );
      const empresas = obtenerValoresUnicos(datasetEmpresas, "Empresa").sort((a, b) =>
        a.localeCompare(b),
      );
      const periodos = obtenerValoresUnicos(datasetPeriodos, "Mes")
        .map(analizarPeriodo)
        .sort((a, b) => a.claveOrden.localeCompare(b.claveOrden));
      const formasPago = obtenerValoresUnicos(datasetFormasPago, "Forma de pago").sort((a, b) =>
        a.localeCompare(b),
      );
      return {
        sucursales,
        empresas,
        periodos,
        formasPago,
      };
    },
    [datasetEmpresas, datasetFormasPago, datasetPeriodos, datasetSucursales],
  );

  useEffect(() => {
    if (filtroSucursal && !opcionesFiltros.sucursales.includes(filtroSucursal)) {
      setFiltroSucursal("");
    }
  }, [filtroSucursal, opcionesFiltros.sucursales]);

  useEffect(() => {
    if (filtroEmpresa && !opcionesFiltros.empresas.includes(filtroEmpresa)) {
      setFiltroEmpresa("");
    }
  }, [filtroEmpresa, opcionesFiltros.empresas]);

  useEffect(() => {
    if (filtroPeriodo && !opcionesFiltros.periodos.some((periodo) => periodo.valor === filtroPeriodo)) {
      setFiltroPeriodo("");
    }
  }, [filtroPeriodo, opcionesFiltros.periodos]);

  useEffect(() => {
    if (filtroFormaPago && !opcionesFiltros.formasPago.includes(filtroFormaPago)) {
      setFiltroFormaPago("");
    }
  }, [filtroFormaPago, opcionesFiltros.formasPago]);

  const filasFiltradas = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return datasetBase.filter((fila) => {
      if (texto) {
        const coincideBusqueda = [
          fila["Apellidos, Nombres"],
          fila["CI N°"],
          fila["Sucursal/ Sector"],
          fila["Puesto"],
          fila["Empresa"],
        ]
          .filter(Boolean)
          .some((campo) => campo?.toString().toLowerCase().includes(texto));
        if (!coincideBusqueda) {
          return false;
        }
      }
      if (filtroSucursal) {
        if (obtenerValorCampo(fila, "Sucursal/ Sector") !== filtroSucursal) {
          return false;
        }
      }
      if (filtroEmpresa) {
        if (obtenerValorCampo(fila, "Empresa") !== filtroEmpresa) {
          return false;
        }
      }
      if (filtroPeriodo) {
        if (obtenerValorCampo(fila, "Mes") !== filtroPeriodo) {
          return false;
        }
      }
      if (filtroFormaPago) {
        if (obtenerValorCampo(fila, "Forma de pago") !== filtroFormaPago) {
          return false;
        }
      }
      return true;
    });
  }, [busqueda, datasetBase, filtroEmpresa, filtroFormaPago, filtroPeriodo, filtroSucursal]);

  const totalPaginas = Math.max(1, Math.ceil(filasFiltradas.length / filasPorPagina));
  const paginaSegura = Math.min(paginaActual, totalPaginas);

  const filasVisibles = useMemo(() => {
    const inicio = (paginaSegura - 1) * filasPorPagina;
    const fin = inicio + filasPorPagina;
    return filasFiltradas.slice(inicio, fin);
  }, [filasFiltradas, paginaSegura, filasPorPagina]);

  const totalRegistros = datasetBase.length;
  const totalProcesado = useMemo(() => {
    return datasetBase.reduce((acumulado, filaActual) => {
      const monto = Number(filaActual["TOTAL INGRESOS"] ?? 0);
      return acumulado + (Number.isFinite(monto) ? monto : 0);
    }, 0);
  }, [datasetBase]);

  const ultimaImportacionTexto = ultimaActualizacion
    ? ultimaActualizacion.toLocaleString("es-PY", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "Sin registros";

  const estadoResumen = useMemo(() => {
    if (errorPlanilla) {
      return { color: "danger" as const, texto: "Con errores" };
    }
    if (cargandoPlanilla) {
      return { color: "warning" as const, texto: "Cargando..." };
    }
    if (totalRegistros === 0) {
      return { color: "secondary" as const, texto: "Sin registros" };
    }
    return { color: "success" as const, texto: "En orden" };
  }, [cargandoPlanilla, errorPlanilla, totalRegistros]);

  const handleExportar = useCallback(() => {
    if (filasFiltradas.length === 0) {
      setExportError("No hay registros para exportar con los filtros aplicados.");
      setExportSuccess(null);
      return;
    }
    setExportando(true);
    setExportError(null);
    setExportSuccess(null);
    try {
      const hoja = XLSX.utils.json_to_sheet(filasFiltradas as Record<string, unknown>[]);
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Planilla");
      const fecha = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const buffer = XLSX.write(libro, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = `planilla-${fecha}.xlsx`;
      document.body.appendChild(enlace);
      enlace.click();
      document.body.removeChild(enlace);
      URL.revokeObjectURL(url);
      setExportSuccess(`Se exportaron ${filasFiltradas.length} registros en formato XLSX.`);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "No se pudo exportar la planilla.");
    } finally {
      setExportando(false);
    }
  }, [filasFiltradas]);

  const handleConfirmarImportacion = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!archivoImport) {
      setImportError("Seleccioná un archivo XLSM para importar.");
      return;
    }
    setImportando(true);
    setImportError(null);
    try {
      const formData = new FormData();
      formData.append("file", archivoImport);
      formData.append("tipoRecibo", tipoRecibo);
      if (periodo.trim()) {
        formData.append("periodo", periodo.trim());
      }
      if (tipoRecibo === "AGUINALDO" && empresaId.trim()) {
        formData.append("empresaId", empresaId.trim());
      }
      if (overwrite) {
        formData.append("overwrite", "1");
      }
      const response = await apiFetch("/api/rrhh/planillas/importar", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data?.ok !== true) {
        let mensaje = data?.error ?? "No se pudo completar la importación.";
        if (data?.code === "PERIODO_EXISTE") {
          const per = data?.periodo ? ` (${data.periodo})` : "";
          mensaje = `El período${per} ya existe. Marcá la opción "Sobrescribir periodo si ya existe" y volvé a importar para reemplazarlo.`;
        }
        throw new Error(mensaje);
      }
      setImportSuccess("La planilla se importó correctamente.");
      setImportModal(false);
      setArchivoImport(null);
      setPeriodo("");
      setEmpresaId("");
      setOverwrite(false);
      void cargarPlanilla();
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "No se pudo importar la planilla.");
    } finally {
      setImportando(false);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <CardBody className="pb-2">
          <Row className="align-items-center g-3">
            <Col lg={4}>
              <h4 className="mb-1">Planilla XLSM</h4>
              <p className="text-muted mb-0">
                Importá archivos, controlá los totales y mantené el historial de actualizaciones.
              </p>
            </Col>
            <Col lg={8}>
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                <Button
                  color="soft-primary"
                  className="d-flex align-items-center gap-1"
                  onClick={() => {
                    setImportModal(true);
                    setImportError(null);
                    setImportSuccess(null);
                  }}
                >
                  <i className="ri-upload-cloud-line" />
                  Importar XLSM
                </Button>
                <Button
                  color="soft-info"
                  className="d-flex align-items-center gap-1"
                  onClick={handleExportar}
                  disabled={exportando || cargandoPlanilla}
                >
                  <i className="ri-download-2-line" />
                  {exportando ? "Exportando..." : "Exportar XLSX"}
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Row className="g-3 mb-3">
        <Col md={3}>
          <Card className="mb-0 h-100">
            <CardBody>
              <p className="text-muted mb-1">Registros importados</p>
              <h5 className="mb-0">
                {cargandoPlanilla ? "Cargando..." : formatNumero(totalRegistros)}
              </h5>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-0 h-100">
            <CardBody>
              <p className="text-muted mb-1">Última importación</p>
              <h5 className="mb-0">{ultimaImportacionTexto}</h5>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-0 h-100">
            <CardBody>
              <p className="text-muted mb-1">Total procesado</p>
              <h5 className="mb-0">Gs. {formatMonto(totalProcesado)}</h5>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mb-0 h-100">
            <CardBody>
              <p className="text-muted mb-1">Estado general</p>
              <Badge color={estadoResumen.color}>{estadoResumen.texto}</Badge>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <Row className="align-items-center g-2 mb-3">
            <Col lg={4} md={6}>
              <Input
                type="search"
                placeholder="Buscar colaborador, documento o área"
                value={busqueda}
                onChange={(event) => setBusqueda(event.target.value)}
              />
            </Col>
            <Col md={6} lg={2}>
              <Input
                type="select"
                value={filtroSucursal}
                onChange={(event) => {
                  setFiltroSucursal(event.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="">Sucursal / Sector (todas)</option>
                {opcionesFiltros.sucursales.map((sucursal) => (
                  <option key={sucursal} value={sucursal}>
                    {sucursal}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6} lg={2}>
              <Input
                type="select"
                value={filtroEmpresa}
                onChange={(event) => {
                  const nuevoValor = event.target.value;
                  const cambio = nuevoValor !== filtroEmpresa;
                  setFiltroEmpresa(nuevoValor);
                  setPaginaActual(1);
                  if (cambio && nuevoValor !== "" && filtroSucursal) {
                    setFiltroSucursal("");
                  }
                }}
              >
                <option value="">Empresa (todas)</option>
                {opcionesFiltros.empresas.map((empresa) => (
                  <option key={empresa} value={empresa}>
                    {empresa}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6} lg={2}>
              <Input
                type="select"
                value={filtroPeriodo}
                onChange={(event) => {
                  setFiltroPeriodo(event.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="">Periodo (todos)</option>
                {opcionesFiltros.periodos.map((periodo) => (
                  <option key={periodo.valor} value={periodo.valor}>
                    {periodo.etiqueta}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={6} lg={2}>
              <Input
                type="select"
                value={filtroFormaPago}
                onChange={(event) => {
                  setFiltroFormaPago(event.target.value);
                  setPaginaActual(1);
                }}
              >
                <option value="">Forma de pago (todas)</option>
                {opcionesFiltros.formasPago.map((forma) => (
                  <option key={forma} value={forma}>
                    {forma}
                  </option>
                ))}
              </Input>
            </Col>
            <Col lg="auto" className="ms-auto text-muted">
              {cargandoPlanilla ? (
                <span className="text-muted">Cargando planilla...</span>
              ) : (
                `${filasFiltradas.length} registros · Página ${paginaSegura} / ${totalPaginas}`
              )}
              <Button color="link" className="p-0 ms-3 align-baseline" onClick={() => void cargarPlanilla()}>
                Actualizar
              </Button>
              <Input
                type="select"
                bsSize="sm"
                className="ms-2 w-auto d-inline-block"
                value={filasPorPagina}
                onChange={(event) => {
                  setFilasPorPagina(Number(event.target.value) || 5);
                  setPaginaActual(1);
                }}
              >
                {[5, 10, 25, 50, 100].map((tam) => (
                  <option key={tam} value={tam}>
                    {tam} por página
                  </option>
                ))}
              </Input>
            </Col>
          </Row>

          {errorPlanilla ? (
            <Alert color="danger" toggle={() => setErrorPlanilla(null)}>
              {errorPlanilla}
            </Alert>
          ) : null}
          {importSuccess ? (
            <Alert color="success" toggle={() => setImportSuccess(null)}>
              {importSuccess}
            </Alert>
          ) : null}
          {importError ? (
            <Alert color="danger" toggle={() => setImportError(null)}>
              {importError}
            </Alert>
          ) : null}
          {exportSuccess ? (
            <Alert color="info" toggle={() => setExportSuccess(null)}>
              {exportSuccess}
            </Alert>
          ) : null}
          {exportError ? (
            <Alert color="danger" toggle={() => setExportError(null)}>
              {exportError}
            </Alert>
          ) : null}

          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 48 }}>
                    <div className="form-check">
                      <Input type="checkbox" className="form-check-input" />
                    </div>
                  </th>
                  <th style={{ minWidth: 180 }}>Colaborador</th>
                  <th style={{ minWidth: 180 }}>Documento y área</th>
                  <th style={{ minWidth: 190 }}>Ingresos base</th>
                  <th style={{ minWidth: 190 }}>Ingresos extras</th>
                  <th style={{ minWidth: 190 }}>Descuentos</th>
                  <th style={{ minWidth: 200 }}>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {filasVisibles.map((fila, index) => {
                  const key = (fila?.LiquidacionId as string | number) ?? `fila-${index}`;
                  const ingresosBase = construirResumenCampos(fila, RESUMEN_INGRESOS_BASE, 5);
                  const ingresosExtras = construirResumenCampos(fila, RESUMEN_INGRESOS_EXTRAS, 5);
                  const descuentos = construirResumenCampos(fila, RESUMEN_DESCUENTOS, 5);
                  const observaciones = construirResumenCampos(fila, RESUMEN_OBSERVACIONES, 5);
                  return (
                    <tr key={key}>
                      <td>
                        <div className="form-check mb-0">
                          <Input type="checkbox" className="form-check-input" />
                        </div>
                      </td>
                      <td className="align-top">
                        <div className="fw-medium">{String(fila["Apellidos, Nombres"] ?? "Sin nombre")}</div>
                        <div className="text-muted small">
                          ID #{String(fila["LiquidacionId"] ?? "—")} · {String(fila["Mes"] ?? "—")}
                        </div>
                        <div className="text-muted small">{String(fila["Empresa"] ?? "—")}</div>
                        <div className="text-muted small">
                          {String(fila["Sucursal/ Sector"] ?? "—")} · {String(fila["Puesto"] ?? "—")}
                        </div>
                      </td>
                      <td className="align-top">
                        <div>{String(fila["CI N°"] ?? "—")}</div>
                        <div className="text-muted small">
                          Forma de pago: {String(fila["Forma de pago"] ?? "No informado")}
                        </div>
                        <div className="text-muted small">Banco: {String(fila["BANCO"] ?? "—")}</div>
                        <div className="text-muted small">
                          Cuenta: {String(fila["N° de cuenta"] ?? "—")}
                        </div>
                      </td>
                      <td className="align-top">
                        {ingresosBase.length > 0 ? (
                          ingresosBase.map((item) => (
                            <div key={item.etiqueta} className="d-flex justify-content-between gap-2 small">
                              <span className="text-muted">{item.etiqueta}</span>
                              <span className="badge bg-table-light text-primary fw-semibold text-end">
                                {item.valor}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-top">
                        {ingresosExtras.length > 0 ? (
                          ingresosExtras.map((item) => (
                            <div key={item.etiqueta} className="d-flex justify-content-between gap-2 small">
                              <span className="text-muted">{item.etiqueta}</span>
                              <span className="badge bg-table-light text-primary fw-semibold text-end">
                                {item.valor}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-top">
                        {descuentos.length > 0 ? (
                          descuentos.map((item) => (
                            <div key={item.etiqueta} className="d-flex justify-content-between gap-2 small">
                              <span className="text-muted">{item.etiqueta}</span>
                              <span className="badge bg-table-light text-primary fw-semibold text-end">
                                {item.valor}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="align-top">
                        {observaciones.length > 0 ? (
                          observaciones.map((item) => (
                            <div key={item.etiqueta} className="d-flex justify-content-between gap-2 small">
                              <span className="text-muted">{item.etiqueta}</span>
                              <span className="fw-semibold text-end">{item.valor}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filasVisibles.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No se encontraron registros con el criterio actual.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted">
                Mostrando {(paginaSegura - 1) * filasPorPagina + 1}-
                {Math.min(paginaSegura * filasPorPagina, filasFiltradas.length)} de {filasFiltradas.length}
              </span>
              <Pagination
                size="sm"
                listClassName="justify-content-end"
                aria-label="Paginación planilla"
              >
                <PaginationItem disabled={paginaSegura <= 1}>
                  <PaginationLink
                    previous
                    onClick={() => setPaginaActual((prev) => Math.max(1, prev - 1))}
                  />
                </PaginationItem>
                {(() => {
                  const buttons: number[] = [];
                  if (totalPaginas <= 5) {
                    for (let i = 1; i <= totalPaginas; i += 1) {
                      buttons.push(i);
                    }
                  } else {
                    let start = paginaSegura - 2;
                    if (start < 1) start = 1;
                    if (start + 4 > totalPaginas) start = totalPaginas - 4;
                    for (let i = 0; i < 5; i += 1) {
                      buttons.push(start + i);
                    }
                  }
                  return buttons;
                })().map((page) => (
                  <PaginationItem key={page} active={page === paginaSegura}>
                    <PaginationLink onClick={() => setPaginaActual(page)}>{page}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={paginaSegura >= totalPaginas}>
                  <PaginationLink
                    next
                    onClick={() => setPaginaActual((prev) => Math.min(totalPaginas, prev + 1))}
                  />
                </PaginationItem>
              </Pagination>
            </div>
          </div>
        </CardBody>
      </Card>

      <Modal isOpen={importModal} toggle={() => setImportModal(!importModal)} centered>
        <ModalHeader toggle={() => setImportModal(!importModal)}>Importar planilla XLSM</ModalHeader>
        <form onSubmit={handleConfirmarImportacion}>
          <ModalBody>
            <FormGroup>
              <Label className="fw-semibold">Archivo XLSM</Label>
              <Input
                type="file"
                accept=".xlsm,.xlsx,.xls"
                onChange={(event) => setArchivoImport(event.target.files?.[0] ?? null)}
                required
              />
            </FormGroup>
            <Row className="g-2">
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">Tipo de recibo</Label>
                  <Input
                    type="select"
                    value={tipoRecibo}
                    onChange={(event) => setTipoRecibo(event.target.value === "AGUINALDO" ? "AGUINALDO" : "SALARIO")}
                  >
                    <option value="SALARIO">Salario</option>
                    <option value="AGUINALDO">Aguinaldo</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-semibold">
                    {tipoRecibo === "AGUINALDO" ? "Periodo (ej. 2025)" : "Periodo (YYYY-MM)"}
                  </Label>
                  {tipoRecibo === "AGUINALDO" ? (
                    <Input
                      type="select"
                      value={periodo}
                      onChange={(event) => setPeriodo(event.target.value)}
                    >
                      <option value="">Seleccioná un periodo</option>
                      {anhosRecientes.map((anho) => (
                        <option key={anho} value={anho}>
                          {`Año: ${anho}`}
                        </option>
                      ))}
                    </Input>
                  ) : (
                    <Input
                      type="select"
                      value={periodo}
                      onChange={(event) => setPeriodo(event.target.value)}
                    >
                      <option value="">Seleccioná periodo (YYYY-MM)</option>
                      {periodosRecientes.map((periodoOption) => (
                        <option key={periodoOption.valor} value={periodoOption.valor}>
                          {periodoOption.etiqueta}
                        </option>
                      ))}
                    </Input>
                  )}
                </FormGroup>
              </Col>
            </Row>
            {tipoRecibo === "AGUINALDO" ? (
              <FormGroup>
                <Label className="fw-semibold">Empresa (ID)</Label>
                <Input
                  type="select"
                  value={empresaId}
                  onChange={(event) => setEmpresaId(event.target.value)}
                >
                  <option value="">Seleccioná una empresa</option>
                  {opcionesFiltros.empresas.map((empresa) => (
                    <option key={empresa} value={empresa}>
                      {empresa}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            ) : null}
            <FormGroup check>
              <Input
                type="checkbox"
                id="importOverwrite"
                checked={overwrite}
                onChange={(event) => setOverwrite(event.target.checked)}
              />
              <Label htmlFor="importOverwrite" check>
                Sobrescribir periodo si ya existe
              </Label>
            </FormGroup>
            {importError ? (
              <Alert color="danger" className="mt-3 mb-0">
                {importError}
              </Alert>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="light"
              onClick={() => {
                setImportModal(false);
              }}
            >
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={importando}>
              {importando ? "Importando..." : "Importar"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </>
  );
};

export default PlanillaTabs;
